import { useRef, useState, useEffect } from 'react';

import PropTypes from 'prop-types';
import { BsCheckCircleFill, BsBuilding, BsGeoAlt, BsPeople, BsStarFill, BsStars } from 'react-icons/bs';

import Button from '#globalComponents/Button/Button';

const KANAL_LABELS = {
	post_mailing: 'Post-Mailing',
	telefon: 'Telefon',
	email: 'E-Mail',
	linkedin: 'LinkedIn',
	phone_manual: 'Telefon',
	manual_custom: 'Manuell',
};

const EMPFEHLUNGSSTAERKE_COLORS = {
	sehr_hoch: 'bg-success-200 text-success-700 border-success-500/40',
	hoch: 'bg-primary-200 text-primary-700 border-primary-200',
	mittel: 'bg-caution-200 text-caution-700 border-caution-500/40',
	niedrig: 'bg-bgBase-400 text-bgBase-800 border-bgBase-500',
};

/**
 * Extracts a display name for the celebration headline: prefers the AI-provided
 * company name, falls back to the bare domain of the analyzed website.
 * @param {object} generation
 * @returns {string|null}
 */
const getCompanyDisplayName = (generation) => {
	// Manual path (no website analyzed): the AI cannot know the company name and
	// tends to invent one — show the generic headline instead.
	const website = generation?.customerWebsite;
	if (!website) return null;
	const aiName = generation?.crmData?.kunde?.name;
	if (aiName) return aiName;
	try {
		return new URL(website).hostname.replace(/^www\./, '');
	} catch {
		return null;
	}
};

const getBannerHeadline = (companyName) =>
	companyName ? `Geschafft — Ihre Strategie für ${companyName} steht!` : 'Geschafft — Ihre Vertriebsstrategie steht!';

const getBannerSubline = (leadCount, isLeadSearchDone) => {
	if (leadCount > 0) {
		return `${leadCount} passende Leads${isLeadSearchDone ? '' : ' und es werden mehr'} — kostenlos für Sie recherchiert.`;
	}
	return isLeadSearchDone
		? 'Ihre Analyse und Outreach-Strategie stehen bereit.'
		: 'Passende Leads werden gerade für Sie recherchiert …';
};

/**
 * Step 3 of onboarding — two-column layout on desktop, tabs on mobile:
 *   Desktop: left = enriched leads (scrolls to match right height), right = outreach strategy
 *   Mobile: tab bar switches between Leads and Strategy views
 */
const OnboardingLeadsView = ({ generation, leads, isLeadSearchDone, onViewLeads }) => {
	const crmData = generation?.crmData;
	const kanaele = crmData?.kanal_priorisierung ?? [];
	const strategyRef = useRef(null);
	const [strategyHeight, setStrategyHeight] = useState(null);
	const [activeTab, setActiveTab] = useState('leads');
	const companyName = getCompanyDisplayName(generation);

	// Measure the right column's natural height and constrain the left to it (desktop only)
	useEffect(() => {
		if (!strategyRef.current) return;
		const mq = window.matchMedia('(min-width: 640px)');
		if (!mq.matches) {
			setStrategyHeight(null);
			return;
		}
		const ro = new ResizeObserver(([entry]) => setStrategyHeight(entry.contentRect.height));
		ro.observe(strategyRef.current);
		return () => ro.disconnect();
	}, []);

	const leadSearchFailed = generation?.leadSearchStatus === 'failed';

	return (
		<div className="space-y-4">
			{/* The goal moment: strategy + first leads are real */}
			<div className="flex items-center gap-3 bg-gradient-to-r from-success-200 to-primary-100 border border-success-500/40 rounded-card px-3 py-3 sm:px-5 sm:py-4">
				<div className="h-9 w-9 sm:h-11 sm:w-11 rounded-full bg-success-700 text-white flex items-center justify-center flex-shrink-0">
					<BsCheckCircleFill className="w-5 h-5 sm:w-6 sm:h-6" />
				</div>
				<div className="min-w-0">
					<p className="text-sm sm:text-md font-bold text-text truncate">{getBannerHeadline(companyName)}</p>
					<p className="text-xs sm:text-sm text-text">{getBannerSubline(leads.length, isLeadSearchDone)}</p>
				</div>
			</div>

			<div className="flex gap-1 bg-bgBase-400 rounded-card p-1 sm:hidden">
				<button
					onClick={() => setActiveTab('leads')}
					className={`flex-1 flex items-center justify-center gap-1.5 py-2 text-sm font-medium rounded-control transition-colors ${
						activeTab === 'leads' ? 'bg-white text-text' : 'text-bgBase-800'
					}`}
				>
					<BsStarFill className="w-3.5 h-3.5" />
					Leads {isLeadSearchDone ? leads.length : '...'}
				</button>
				<button
					onClick={() => setActiveTab('strategy')}
					className={`flex-1 flex items-center justify-center gap-1.5 py-2 text-sm font-medium rounded-control transition-colors ${
						activeTab === 'strategy' ? 'bg-white text-text' : 'text-bgBase-800'
					}`}
				>
					<BsStars className="w-3.5 h-3.5" />
					Strategie
				</button>
			</div>

			{/* Two-column layout (desktop) / tabbed single-column (mobile) */}
			<div className="flex flex-col sm:flex-row gap-4 sm:gap-6 items-start">
				{/* LEFT: Leads list */}
				<div
					className={`w-full sm:w-3/5 flex flex-col min-w-0 ${activeTab !== 'leads' ? 'hidden sm:flex' : ''}`}
					style={strategyHeight ? { height: strategyHeight } : undefined}
				>
					<div className="flex items-center justify-between mb-2 flex-shrink-0">
						<h3 className="text-sm font-semibold text-text flex items-center gap-2">
							{isLeadSearchDone
								? `Ihre ersten ${leads.length} Leads`
								: leads.length > 0
									? `${leads.length} Leads gefunden...`
									: 'Leads werden gesucht...'}
						</h3>
						<span className="text-xs text-bgBase-800 hidden sm:inline">Passend zu Ihrem Profil</span>
					</div>

					<div className="space-y-1.5 overflow-y-auto pr-1 flex-1 min-h-0">
						{leads.map((lead) => (
							<div
								key={lead.id}
								className="flex items-center gap-3 p-3 bg-white border border-bgBase-400 rounded-card hover:border-bgBase-500 transition-colors"
							>
								<div className="h-9 w-9 rounded-control bg-bgBase-400 text-bgBase-800 flex items-center justify-center flex-shrink-0">
									<BsBuilding className="w-4 h-4" />
								</div>
								<div className="flex-1 min-w-0">
									<div className="flex items-center gap-2">
										<span className="text-sm font-medium text-text truncate">
											{lead.companyName}
										</span>
									</div>
									<div className="flex items-center gap-3 text-xs text-bgBase-800 mt-0.5">
										{(lead.addressPostal || lead.addressCity) && (
											<span className="flex items-center gap-0.5">
												<BsGeoAlt className="w-3 h-3" />
												{[lead.addressPostal, lead.addressCity].filter(Boolean).join(' ')}
											</span>
										)}
										{lead.employeeCount && (
											<span className="flex items-center gap-0.5">
												<BsPeople className="w-3 h-3" />
												{lead.employeeCount} MA
											</span>
										)}
									</div>
								</div>
							</div>
						))}

						{!isLeadSearchDone && !leadSearchFailed && (
							<div className="flex items-center gap-3 p-3 text-bgBase-800">
								<div className="h-9 w-9 rounded-control bg-bgBase-300 flex items-center justify-center flex-shrink-0">
									<svg
										className="animate-spin h-4 w-4 text-primary-400"
										viewBox="0 0 24 24"
										fill="none"
									>
										<circle
											className="opacity-25"
											cx="12"
											cy="12"
											r="10"
											stroke="currentColor"
											strokeWidth="4"
										/>
										<path
											className="opacity-75"
											fill="currentColor"
											d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
										/>
									</svg>
								</div>
								<span className="text-sm">Leads werden gesucht...</span>
							</div>
						)}

						{/* Empty state */}
						{isLeadSearchDone && leads.length === 0 && (
							<div className="text-center py-10 text-sm text-bgBase-800">
								{leadSearchFailed
									? 'Lead-Suche fehlgeschlagen. Sie können später manuell Leads suchen.'
									: 'Keine Leads gefunden. Sie können später manuell Leads suchen.'}
							</div>
						)}
					</div>
				</div>

				{/* RIGHT: Strategy — this column's natural height drives the left (desktop) */}
				<div
					ref={strategyRef}
					className={`w-full sm:w-2/5 space-y-4 sm:flex-shrink-0 ${activeTab !== 'strategy' ? 'hidden sm:block' : ''}`}
				>
					{crmData?.analyse_zusammenfassung && (
						<div>
							<h4 className="text-xs font-semibold text-bgBase-800 uppercase tracking-wider mb-3">
								Analyse
							</h4>
							<div className="bg-bgBase-300 border border-bgBase-400 rounded-card p-3 sm:p-4">
								<p className="text-sm text-text leading-relaxed">{crmData.analyse_zusammenfassung}</p>
							</div>
						</div>
					)}

					{kanaele.length > 0 && (
						<div>
							<h4 className="text-xs font-semibold text-bgBase-800 uppercase tracking-wider mb-3">
								Priorisierte Outreach-Strategie
							</h4>
							<div className="space-y-2">
								{kanaele.map((kanal) => (
									<div
										key={kanal.rang}
										className="flex items-start gap-2.5 p-3 bg-white border border-bgBase-400 rounded-card"
									>
										<div className="h-6 w-6 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center flex-shrink-0 text-xs font-bold">
											{kanal.rang}
										</div>
										<div className="flex-1 min-w-0">
											<div className="flex items-center gap-2 mb-0.5">
												<span className="text-sm font-medium text-text">
													{KANAL_LABELS[kanal.kanal] ?? kanal.kanal}
												</span>
												<span
													className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium border ${EMPFEHLUNGSSTAERKE_COLORS[kanal.empfehlungsstaerke] ?? EMPFEHLUNGSSTAERKE_COLORS.niedrig}`}
												>
													{kanal.empfehlungsstaerke?.replace('_', ' ')}
												</span>
											</div>
											<p className="text-xs text-bgBase-800 leading-relaxed">
												{kanal.begruendung}
											</p>
											{kanal.erwartete_response_rate_prozent && (
												<p className="text-[11px] text-bgBase-800 mt-0.5">
													Response: {kanal.erwartete_response_rate_prozent}
												</p>
											)}
										</div>
									</div>
								))}
							</div>
						</div>
					)}
				</div>
			</div>

			<div className="flex items-center justify-end gap-3 pt-4 border-t border-bgBase-500 fixed bottom-0 left-0 right-0 bg-white px-4 py-3 z-10 sm:static sm:px-0 sm:py-0 sm:pt-4 sm:bg-transparent">
				{!isLeadSearchDone && (
					<span className="text-xs text-bgBase-800 hidden sm:inline">
						Die Suche läuft im Hintergrund weiter.
					</span>
				)}
				<Button
					className="flex-1 sm:flex-none"
					variant="solid"
					onClick={onViewLeads}
					disabled={!isLeadSearchDone && leads.length === 0}
				>
					{leads.length > 0
						? `${leads.length} Leads jetzt ansehen →`
						: isLeadSearchDone
							? 'Zur Lead-Suche'
							: 'Leads werden gesucht …'}
				</Button>
			</div>
		</div>
	);
};

OnboardingLeadsView.propTypes = {
	generation: PropTypes.shape({
		crmData: PropTypes.object,
		leadSearchStatus: PropTypes.string,
		customerWebsite: PropTypes.string,
	}).isRequired,
	leads: PropTypes.arrayOf(PropTypes.object).isRequired,
	isLeadSearchDone: PropTypes.bool.isRequired,
	onViewLeads: PropTypes.func.isRequired,
};

export default OnboardingLeadsView;
